import { useState, useEffect } from 'react';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { 
  Plus, ChevronLeft, ChevronRight, Edit, Trash2, 
  Users, UserCheck, UserX, Shield, Search, Copy
} from 'lucide-react';
import { toast } from 'sonner';

const initialFormData = {
  username: '',
  email: '',
  password: '',
  full_name: '',
  role_id: '1',
  is_active: 1,
};

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  
  // Filters
  const [filters, setFilters] = useState({
    username: '',
    email: '',
    role_id: '',
    is_active: '',
  });
  
  // Form options
  const [roles, setRoles] = useState([]);
  
  // CRUD State
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/admin/users/options');
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pagination.page, limit: pagination.limit });
      
      if (filters.username) params.append('username_filter', filters.username);
      if (filters.email) params.append('email_filter', filters.email);
      if (filters.role_id && filters.role_id !== 'all') params.append('role_id', filters.role_id);
      if (filters.is_active && filters.is_active !== 'all') params.append('is_active', filters.is_active);

      const response = await api.get(`/admin/users?${params}`);
      setUsers(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRoles(); }, []);
  useEffect(() => { fetchUsers(); }, [pagination.page]);
  
  // Debounced filter effect
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchUsers();
    }, 800);
    return () => clearTimeout(t);
  }, [filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // CRUD Handlers
  const handleCreate = () => {
    setEditingItem(null);
    setFormData(initialFormData);
    setModalOpen(true);
  };

  const handleEdit = (user) => {
    console.log('Editing user raw data:', JSON.stringify(user)); // Debug log
    
    // Try to find the user ID with different possible field names
    const userId = user.id ?? user.ID ?? user.user_id ?? user.userId;
    
    if (!userId && userId !== 0) {
      console.error('User has no valid ID. Available fields:', Object.keys(user));
      toast.error(`Error: El usuario no tiene ID válido. Campos disponibles: ${Object.keys(user).join(', ')}`);
      return;
    }
    
    // Normalize the user object to always have 'id'
    const normalizedUser = { ...user, id: userId };
    
    setEditingItem(normalizedUser);
    setFormData({
      username: user.username || '',
      email: user.email || '',
      password: '', // Don't show password
      full_name: user.full_name || '',
      role_id: user.role_id ? String(user.role_id) : (roles.length > 0 ? String(roles[0].id) : '1'),
      is_active: user.is_active !== null && user.is_active !== undefined ? user.is_active : 1,
    });
    setModalOpen(true);
  };

  const handleCloneUser = (user) => {
    // Abrir modal de nuevo usuario pre-llenado con datos copiables
    setEditingItem(null); // Es creación, no edición
    setFormData({
      username: '', // Debe ser único - vacío
      email: '', // Debe ser único - vacío
      password: '', // Debe ser nuevo - vacío
      full_name: `${user.full_name || user.username} (Copia)`,
      role_id: user.role_id ? String(user.role_id) : '1',
      is_active: user.is_active ?? 1,
    });
    setModalOpen(true);
    toast.info('Complete usuario, email y contraseña para el nuevo usuario');
  };

  const handleSave = async () => {
    if (!formData.username.trim() || !formData.email.trim()) {
      toast.error('Usuario y email son requeridos');
      return;
    }
    
    if (!editingItem && !formData.password.trim()) {
      toast.error('La contraseña es requerida para nuevos usuarios');
      return;
    }

    setSaving(true);
    try {
      let payload = {};
      
      if (editingItem) {
        // Validate editingItem has valid ID
        if (!editingItem.id && editingItem.id !== 0) {
          toast.error('Error: No se puede actualizar - ID de usuario inválido');
          console.error('editingItem has no valid ID:', editingItem);
          setSaving(false);
          return;
        }
        
        // For edits, only send changed fields
        if (formData.username !== editingItem.username) {
          payload.username = formData.username;
        }
        if (formData.email !== editingItem.email) {
          payload.email = formData.email;
        }
        if (formData.password && formData.password.trim()) {
          payload.password = formData.password;
        }
        if ((formData.full_name || '') !== (editingItem.full_name || '')) {
          payload.full_name = formData.full_name || null;
        }
        if (parseInt(formData.role_id) !== editingItem.role_id) {
          payload.role_id = parseInt(formData.role_id);
        }
        if (formData.is_active !== editingItem.is_active) {
          payload.is_active = formData.is_active;
        }
        
        // Check if there are any changes
        if (Object.keys(payload).length === 0) {
          toast.info('No hay cambios para guardar');
          setModalOpen(false);
          return;
        }
        
        console.log('Updating user ID:', editingItem.id, 'with payload:', payload);
        await api.put(`/admin/users/${editingItem.id}`, payload);
        toast.success('Usuario actualizado exitosamente');
      } else {
        // For new users, send all fields
        payload = {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name || null,
          role_id: parseInt(formData.role_id),
          is_active: formData.is_active,
        };
        await api.post('/admin/users', payload);
        toast.success('Usuario creado exitosamente');
      }
      
      setModalOpen(false);
      fetchUsers();
    } catch (error) {
      // Handle error message - FastAPI returns detail as string or array of objects
      let msg = 'Error al guardar';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          msg = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          // Validation errors from FastAPI
          msg = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          msg = detail.msg || JSON.stringify(detail);
        }
      }
      toast.error(msg);
      console.error('Error saving user:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success('Usuario eliminado exitosamente');
      setDeleteConfirm(null);
      fetchUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al eliminar usuario';
      toast.error(msg);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Stats
  const activeUsers = users.filter(u => u.is_active).length;
  const inactiveUsers = users.filter(u => !u.is_active).length;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Usuarios</h1>
            <p className="page-subtitle">Gestión de usuarios del sistema</p>
          </div>
          <Button className="btn btn-primary" onClick={handleCreate} data-testid="add-user-btn">
            <Plus className="h-4 w-4" />
            Nuevo Usuario
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="stats-card">
            <div className="mb-2">
              <Users className="h-8 w-8 text-primary" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value">{pagination.total}</div>
            <div className="stats-card-label">Total usuarios</div>
          </div>
          <div className="stats-card">
            <div className="mb-2">
              <UserCheck className="h-8 w-8 text-primary" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value">{activeUsers}</div>
            <div className="stats-card-label">Activos</div>
          </div>
          <div className="stats-card">
            <div className="mb-2">
              <UserX className="h-8 w-8 text-primary" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value">{inactiveUsers}</div>
            <div className="stats-card-label">Inactivos</div>
          </div>
        </div>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th>USUARIO</th>
                        <th>NOMBRE</th>
                        <th>EMAIL</th>
                        <th>ROL</th>
                        <th>ESTADO</th>
                        <th className="w-20 text-right">ACCIONES</th>
                      </tr>
                      {/* Filters Row */}
                      <tr className="bg-muted/30">
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Filtrar..."
                            value={filters.username}
                            onChange={(e) => handleFilterChange('username', e.target.value)}
                            className="h-7 text-xs bg-background"
                          />
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Filtrar..."
                            value={filters.email}
                            onChange={(e) => handleFilterChange('email', e.target.value)}
                            className="h-7 text-xs bg-background"
                          />
                        </th>
                        <th className="py-2 px-4">
                          <Select value={filters.role_id || "all"} onValueChange={(v) => handleFilterChange('role_id', v)}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              {roles.map(r => (
                                <SelectItem key={r.id} value={String(r.id)}>{r.role_name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4">
                          <Select value={filters.is_active || "all"} onValueChange={(v) => handleFilterChange('is_active', v)}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              <SelectItem value="1">Activos</SelectItem>
                              <SelectItem value="0">Inactivos</SelectItem>
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="w-28"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan={6}>
                            <div className="empty-state">
                              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron usuarios</p>
                            </div>
                          </td>
                        </tr>
                      ) : users.map((user) => (
                        <tr key={user.id} data-testid={`user-row-${user.username}`}>
                          <td className="font-medium">
                            <div className="flex items-center gap-2">
                              <span className="text-primary">{user.username}</span>
                              {user.is_admin === 1 && (
                                <Shield className="h-3.5 w-3.5 text-primary" title="Administrador" />
                              )}
                            </div>
                          </td>
                          <td className="text-muted-foreground">{user.full_name || '-'}</td>
                          <td className="text-muted-foreground">{user.email}</td>
                          <td>
                            <span className="badge badge-primary">{user.role_name || 'Sin rol'}</span>
                          </td>
                          <td>
                            <span className={`badge ${user.is_active ? 'badge-success' : 'badge-error'}`}>
                              {user.is_active ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td className="w-28 text-right">
                            <div className="flex justify-end gap-0.5">
                              <button 
                                onClick={() => handleCloneUser(user)} 
                                className="action-btn" 
                                title="Clonar usuario"
                                data-testid={`clone-user-${user.username}`}
                              >
                                <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
                              </button>
                              <button 
                                onClick={() => handleEdit(user)} 
                                className="action-btn action-btn-edit" 
                                title="Editar"
                                data-testid={`edit-user-${user.username}`}
                              >
                                <Edit className="h-3.5 w-3.5" strokeWidth={1.5} />
                              </button>
                              <button 
                                onClick={() => setDeleteConfirm(user)} 
                                className="action-btn-delete" 
                                title="Eliminar"
                                data-testid={`delete-user-${user.username}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-t border-border/30">
                  <p className="text-sm text-muted-foreground">
                    {pagination.total > 0 ? `${((pagination.page - 1) * pagination.limit) + 1} - ${Math.min(pagination.page * pagination.limit, pagination.total)} de ${pagination.total}` : '0 resultados'}
                  </p>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1} className="h-8 w-8">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm px-2">{pagination.page} / {pagination.pages || 1}</span>
                    <Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page >= pagination.pages} className="h-8 w-8">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="modal-content max-w-md">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                {editingItem ? 'Editar Usuario' : 'Nuevo Usuario'}
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4 space-y-4">
              <div>
                <Label className="text-sm font-medium">Usuario *</Label>
                <Input 
                  value={formData.username} 
                  onChange={(e) => handleInputChange('username', e.target.value)} 
                  className="input-subtle mt-1.5" 
                  placeholder="ej: jperez"
                  data-testid="input-username"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Email *</Label>
                <Input 
                  type="email"
                  value={formData.email} 
                  onChange={(e) => handleInputChange('email', e.target.value)} 
                  className="input-subtle mt-1.5" 
                  placeholder="usuario@empresa.com"
                  data-testid="input-email"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">
                  Contraseña {editingItem ? '(dejar vacío para mantener)' : '*'}
                </Label>
                <Input 
                  type="password"
                  value={formData.password} 
                  onChange={(e) => handleInputChange('password', e.target.value)} 
                  className="input-subtle mt-1.5" 
                  placeholder={editingItem ? '••••••••' : 'Contraseña segura'}
                  data-testid="input-password"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Nombre Completo</Label>
                <Input 
                  value={formData.full_name} 
                  onChange={(e) => handleInputChange('full_name', e.target.value)} 
                  className="input-subtle mt-1.5" 
                  placeholder="Juan Pérez"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Rol</Label>
                <Select value={formData.role_id} onValueChange={(v) => handleInputChange('role_id', v)}>
                  <SelectTrigger className="input-subtle mt-1.5">
                    <SelectValue placeholder="Seleccionar rol...">
                      {/* Show current role name if available */}
                      {formData.role_id && roles.find(r => String(r.id) === formData.role_id)?.role_name}
                      {formData.role_id && !roles.find(r => String(r.id) === formData.role_id) && editingItem?.role_name}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {roles.length === 0 ? (
                      <SelectItem value={formData.role_id || "1"} disabled>Cargando roles...</SelectItem>
                    ) : (
                      roles.map(r => (
                        <SelectItem key={r.id} value={String(r.id)}>{r.role_name}</SelectItem>
                      ))
                    )}
                    {/* If current role_id is not in the list, add it */}
                    {editingItem && editingItem.role_id && roles.length > 0 && !roles.find(r => r.id === editingItem.role_id) && (
                      <SelectItem value={String(editingItem.role_id)}>{editingItem.role_name || `Rol ID ${editingItem.role_id}`}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Usuario Activo</Label>
                <Switch 
                  checked={formData.is_active === 1}
                  onCheckedChange={(checked) => handleInputChange('is_active', checked ? 1 : 0)}
                />
              </div>
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</Button>
              <Button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="modal-content">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4">
              <p className="text-muted-foreground">
                ¿Está seguro que desea eliminar al usuario <span className="font-semibold text-foreground">{deleteConfirm?.username}</span>?
              </p>
              <p className="text-sm text-destructive mt-2">
                Esta acción no se puede deshacer.
              </p>
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
              <Button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default UsersPage;
